import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { AnalystSnapshotResponse, AnalystSnapshotSection } from '../../types/aiAnalyst';

const toneClass = (value: number) => (value > 0 ? 'text-emerald-400' : value < 0 ? 'text-rose-400' : 'text-slate-400');

const badgeClass = (severity?: string) => {
  switch (severity) {
    case 'success':
    case 'info':
      return 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30';
    case 'warning':
      return 'bg-amber-500/15 text-amber-300 border-amber-400/30';
    case 'error':
      return 'bg-rose-500/15 text-rose-300 border-rose-400/30';
    case 'action':
      return 'bg-sky-500/15 text-sky-300 border-sky-400/30';
    default:
      return 'bg-slate-700/40 text-slate-300 border-white/10';
  }
};

const pickCellValue = (row: Record<string, unknown>, column: string) => {
  const normalizedRowEntries = Object.entries(row).map(([key, value]) => [key.toLowerCase().replace(/[^a-z0-9]/g, ''), value] as const);
  const normalizedRow = new Map<string, unknown>(normalizedRowEntries);

  const map: Record<string, string[]> = {
    holding: ['holdingLabel', 'holding', 'key', 'symbol', 'propertyName', 'accountName', 'category', 'metric'],
    shares: ['shares', 'quantity'],
    'avg cost': ['avgCost', 'costBasis'],
    'live price': ['livePrice', 'currentPrice'],
    'market value': ['marketValue', 'value', 'equity'],
    'gain/loss': ['gainLoss', 'gainLossAbs'],
    return: ['returnPct', 'gainLossPct'],
    category: ['category', 'metric', 'holdingLabel'],
    value: ['value', 'marketValue', 'fireNumber', 'fiProgressPct'],
    notes: ['notes', 'subLabel'],
  };

  const normalized = column.toLowerCase().trim();
  const candidateKeys = map[normalized] || [normalized.replace(/[^a-z0-9]/g, '')];
  for (const key of candidateKeys) {
    const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (normalizedRow.has(normalizedKey)) return normalizedRow.get(normalizedKey);
  }
  return null;
};

const renderCell = (value: unknown) => {
  if (typeof value === 'number') return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const renderTable = (section: AnalystSnapshotSection) => (
  <div className="bg-[#1e1e26] rounded-2xl border border-white/10 shadow-lg overflow-hidden" key={section.id}>
    <div className="px-5 py-4 border-b border-white/10">
      <h3 className="text-white font-semibold">{section.title}</h3>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#16161d] text-slate-300">
          <tr>{(section.columns || []).map((column) => <th key={column} className="text-left p-3">{column}</th>)}</tr>
        </thead>
        <tbody>
          {(section.rows || []).map((rawRow, index) => {
            const row = rawRow as Record<string, unknown>;
            const gainLoss = Number(row.gainLoss ?? row.gainLossAbs ?? 0);
            const returnPct = Number(row.returnPct ?? row.gainLossPct ?? 0);
            const flags = Array.isArray(row.flags) ? row.flags : [];
            return (
              <tr key={`${section.id}-${index}`} className="border-t border-white/5 text-slate-200">
                {(section.columns || []).map((column, idx) => {
                  const value = pickCellValue(row, column);
                  const isGainCell = /gain\/loss/i.test(column);
                  const isReturnCell = /return/i.test(column);
                  return (
                    <td key={`${column}-${idx}`} className={`p-3 ${isGainCell ? toneClass(gainLoss) : isReturnCell ? toneClass(returnPct) : ''}`}>
                      {idx === 0 && flags.length > 0 && <AlertTriangle size={14} className="inline mr-1 text-amber-400" />}
                      {renderCell(value)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    {section.totals && (
      <div className="m-4 rounded-xl border border-white/10 bg-[#14141b] p-4 text-sm text-slate-300">
        {Object.entries(section.totals).map(([key, value]) => (
          <div key={key} className="flex justify-between py-1">
            <span className="capitalize">{key}</span>
            <span>{renderCell(value)}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const renderBullets = (section: AnalystSnapshotSection) => (
  <div className="bg-[#1e1e26] rounded-2xl border border-white/10 shadow-lg p-5" key={section.id}>
    <h3 className="text-white font-semibold mb-3">{section.title}</h3>
    <ul className="space-y-2">
      {(section.items || []).map((item, index) => (
        <li key={`${section.id}-${index}`} className="flex items-start gap-2 text-slate-200">
          <span className={`mt-0.5 rounded-full border px-2 py-0.5 text-[11px] ${badgeClass(item.severity)}`}>{item.severity || 'note'}</span>
          <span>{item.text}</span>
        </li>
      ))}
    </ul>
  </div>
);

interface Props {
  snapshot: AnalystSnapshotResponse;
}

export default function AnalystSnapshot({ snapshot }: Props) {
  return (
    <div className="space-y-5">
      <div className="bg-gradient-to-r from-sky-500/15 to-purple-500/10 border border-white/10 rounded-2xl p-5">
        <div className="text-xl text-white font-bold">{snapshot.title}</div>
        <div className="text-sm text-slate-300">Powered by Gemini • {new Date(snapshot.asOf).toLocaleString()}</div>
      </div>
      {snapshot.aiError && (
        <div className="rounded-2xl border border-rose-400/40 bg-rose-500/10 p-4 text-sm text-rose-200">
          {snapshot.aiError}
        </div>
      )}
      {snapshot.sections.map((section) => (section.type === 'table' ? renderTable(section) : renderBullets(section)))}
    </div>
  );
}
